import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import { Table, Spinner, Dropdown } from 'react-bootstrap';
import DataTable, { useDataTableHandle } from '../../DataTable';
import LicenceModal from './licenceModal';
import ToastNotification from '../../Toast/ToastNotification';
import { RefDataContext } from '../Compliance';
import { getLicencesListLazy } from '../../../api/compliance';
import { getNewLoad } from '../../apiCalls';
import { ExternalLinkAlt } from '@styled-icons/fa-solid';
import { StoreContext } from '../../Store';
import ResetButton from '../../Buttons/ResetButton';
import MasterFilters from '../../MasterFilters/MasterFilters';

/** Build filter object with plain keys (portType, portName, etc.) for the backend. Strips "filter_" prefix if present. */
function buildBackendFilter(search, simpleFilter) {
  const merged = { ...search, ...(simpleFilter && typeof simpleFilter === 'object' ? simpleFilter : {}) };
  const out = {};
  Object.entries(merged).forEach(([key, value]) => {
    const plainKey = key.startsWith('filter_') ? key.slice(7) : key;
    out[plainKey] = value;
  });
  return out;
}

const Licences = () => {
  const [licences, setLicences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [directions, setDirections] = useState([]);
  const [selectedLicence, setSelectedLicence] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({ type: 'success', message: '' });
  const lastSearchRef = useRef(null);
  const lastFilterRef = useRef(null);
  const [filterData, setFilterData] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const store = useContext(StoreContext);
  const persistedFilters = store.complianceFilters[0].filters || {};

  const dth = useDataTableHandle();
  const { registerAddHandler } = useContext(RefDataContext);
  const hasFiltersApplied = !!Object.keys(persistedFilters).length || dth?.current?.sortStatus?.currentKey;

  useEffect(() => {
    if (hasFiltersApplied) {
      setFilterData(store.complianceFilters[0].filters);
      setShowFilters(true);
    }
  }, []);

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus({});
    store.complianceFilters[1]({});
    setFilterData({});
    await updateData({}, true, true);
  };

  const handleSubmit = async () => {
    store.complianceFilters[1]({ filters: { ...filterData } });
    await updateData(
      dth.current.setServerDataLength,
      dth.current.pagination,
      dth.current.sortStatus,
      lastSearchRef.current ?? null,
      filterData,
      true
    );
  };

  useEffect(() => {
    registerAddHandler('licences', () =>
      setSelectedLicence({
        id: -1,
        documentCode: '',
        description: '',
        skillsMatrix: 10,
        disabled: '',
        createdBy: '',
        createdDate: '',
        lastUpdatedBy: '',
        lastUpdatedDate: '',
      })
    );
  }, [registerAddHandler]);

  const updateData = useCallback(async (setPaginationLength, pagination, sortStatus, search, filter, resetSort) => {
    lastSearchRef.current = search ?? null;
    lastFilterRef.current = filter ?? null;
    const sortStatus1 = resetSort ? {} : sortStatus || dth.current.sortStatus;
    const backendFilter = buildBackendFilter(search ?? {}, filter ?? {});
    const payload = {
      skip: pagination.pageNo * pagination.itemsPerPage,
      amount: pagination.itemsPerPage,
      sortColumn: sortStatus1?.currentKey ?? 'portName',
      sortDirection: sortStatus1?.direction ?? 'asc',
      filter: Object.keys(backendFilter).length ? backendFilter : undefined,
    };
    try {
      setLoading(true);
      const result = await getLicencesListLazy(payload);
      setLicences(result ?? []);
      setPaginationLength(result?.[0]?.totalItems ?? 0);
    } catch (err) {
      console.error('Failed to load licence list', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshLicences = useCallback(() => {
    if (!dth?.current) return;
    updateData(
      dth.current.setServerDataLength,
      dth.current.pagination,
      dth.current.sortStatus,
      lastSearchRef.current ?? null,
      lastFilterRef.current ?? null
    );
  }, [updateData, dth]);

  useEffect(() => {
    if (!paginationLoaded || !dth?.current?.pagination?.itemsPerPage) return;

    updateData(
      dth.current.setServerDataLength,
      dth.current.pagination,
      dth.current.sortStatus,
      lastSearchRef.current ?? null,
      lastFilterRef.current ?? null
    );
  }, [paginationLoaded, updateData]);

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        type={toastConfig.type}
        message={toastConfig.message}
      />

      <LicenceModal
        show={!!selectedLicence}
        licence={selectedLicence}
        existingLicences={licences}
        onHide={() => setSelectedLicence(null)}
        onSaved={(isNew) => {
          setToastConfig({
            type: 'success',
            message: isNew ? 'New Licence added' : 'Licence updated',
          });
          setShowToast(true);
          setSelectedLicence(null);
          refreshLicences();
        }}
      />

      <div className="d-flex justify-content-between px-3 pt-4">
        <h1 className="page-heading">Licences</h1>
        <div className="d-flex gap-4 align-items-center">
          {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
          <button
            className="stepper-button"
            onClick={() => setShowFilters(!showFilters)}
            style={{ minWidth: 145, maxWidth: 145 }}
            disabled={hasFiltersApplied && showFilters}
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>
      </div>
      <MasterFilters
        visible={showFilters}
        values={filterData}
        // defaultValues={{ defaultCreatedFrom, defaultCreatedTo }}
        onChange={setFilterData}
        onSubmit={handleSubmit}
      />

      {loading && (
        <span className="d-flex align-items-center gap-2 mb-2">
          <Spinner size="sm" animation="border" /> Loading...
        </span>
      )}
      <div className="px-3 pt-4">
        <DataTable
          idKey="id"
          handle={dth}
          data={licences}
          className="table-striped"
          sortable
          lazyLoading
          advancedSearch
          // showAdvnSearch
          onUpdate={updateData}
          onPaginationLoaded={() => setPaginationLoaded(true)}
          isLoading={loading}
          handleReset={() => {
            lastSearchRef.current = null;
            lastFilterRef.current = null;
          }}
        >
          <DataTable.Headers
            style={{
              fontSize: '14.4px',
              backgroundColor: '#fff',
              paddingBottom: '10px',
              borderBottom: '1px solid #ddd',
              borderTop: '1px solid #ddd',
              whiteSpace: 'nowrap',
            }}
          />
          <DataTable.Pagination itemsPerPage={[10, 25, 50, 100]} className="w-100 d-flex justify-content-end" />
          <DataTable.Row style={{ fontSize: '15px' }} />
          <DataTable.Column
            colKey="documentCode"
            header="Document Code"
            searchOpts={{ type: 'text', sendAs: 'filter_documentCode' }}
          />
          <DataTable.Column
            colKey="description"
            header="Description"
            searchOpts={{ type: 'text', sendAs: 'filter_description' }}
          />
          <DataTable.Column
            colKey="lastModified"
            header="Last Updated"
            searchOpts={{ type: 'date', sendAs: 'filter_lastModified' }}
          />
          <DataTable.Column
            colKey="disabled"
            header="Disabled"
            searchOpts={{ type: 'text', sendAs: 'filter_disabled' }}
          />
          <DataTable.Column
            headerStyle={{ textAlign: 'center', width: '50px' }}
            header="Edit"
            children={(item) => (
              <Dropdown className="w-100 h-100 d-grid center">
                <Dropdown.Toggle as="button" className="dot-menu-button" onClick={() => setSelectedLicence(item)}>
                  <ExternalLinkAlt width="1.1rem" height="1.1rem" color="#00857d" />
                </Dropdown.Toggle>
              </Dropdown>
            )}
          />
        </DataTable>
      </div>
    </>
  );
};

export default Licences;
